import React, { useMemo } from 'react';

import {
  Flex,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';

import { Icon, StatusBadge } from 'components';
import { ActivityStatus } from 'enums/status';
import { getPercentage } from 'utils/parsers';

export default function RouteAccordion({
  code,
  name,
  kmCount,
  completedActivities,
  activitiesCount,
  activities,
  isExpanded,
  toggle,
  ...route
}) {
  const completed = useMemo(
    () => getPercentage(completedActivities, activitiesCount)
      .toFixed(0),
    [completedActivities, activitiesCount]);

  const isAlert = useMemo(
    () => activities.find((activity) => activity.status === ActivityStatus.Alert),
    [activities]);

  return (
    <Stack
      as={motion.div}
      variants={{
        visible: { height: 'auto' },
        hidden: { height: 80 },
      }}
      initial="hidden"
      overflow="hidden"
      animate={isExpanded ? 'visible' : 'hidden'}
      bg="white.default"
      alignItems="center"
      borderRadius={8}
      border="none"
      width="100%">
      <Stack
        onClick={toggle}
        borderColor="gray.50"
        p={4}
        borderBottom={isExpanded ? '1px' : 'none'}
        justifyContent="space-between"
        width="100%">
        <Flex
          width="100%"
          justifyContent="space-between">
          <HStack>
            <Text
              cursor="pointer"
              as="a"
              href={`/routes/${route.routeId}`}
              variant="mediumBold"
              color="gray.600">
              {code}
            </Text>
            <Text
              variant="smallSemiBold"
              color="gray.500">
              {name}
            </Text>
          </HStack>
          <HStack>
            <Text
              variant="xSmallSemiBold"
              color="gray.500">
              Km Total Previsto: {kmCount} km
            </Text>
            <Text
              variant="xSmallSemiBold"
              color="gray.500">
              {`Atividades: ${completedActivities}/${activitiesCount}`}
            </Text>
            <StatusBadge
              label={route.status.toLocaleUpperCase()}
              iconName="clock"
              status={route.status}
            />
            <Icon
              name="chevron-down"
              color="gray.600"
            />
          </HStack>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="space-between">
          <Flex
            mr={4}
            borderRadius={8}
            height={2}
            width="100%"
            bg="gray.50">
            <Flex
              as={motion.div}
              borderRadius={8}
              height={2}
              initial={{ width: 0 }}
              exit={{ width: 0 }}
              animate={{ width: `${completed}%` }}
              bg={isAlert ? 'system.error.default' : 'primary.default'}
            />
          </Flex>
          <Text
            variant="smallBold"
            color={isAlert ? 'system.error.default' : 'primary.default'}>
            {completed}%
          </Text>
        </Flex>
      </Stack>
      <Stack
        p={4}
        width="100%">
        {activities.map(({
          type, vehicle, dueDate, drivers, status, finishedAt,
        }, index) => (
          <HStack
            spacing={4}
            alignItems="flex-start"
            width="100%">
            <Stack
              width={10}
              align="center">
              <Text
                variant="smallBold"
                color="gray.600">
                {dayjs(finishedAt ?? dueDate).format('HH:mm') ?? 'N/A'}
              </Text>
              <Flex
                hidden={index === activities.length - 1}
                w="px"
                h="30px"
                bg="gray.400"
              />
            </Stack>
            <Stack flex={1}>
              <Text
                variant="smallBold"
                color="gray.600">
                {type}
              </Text>
              <HStack>
                <Text
                  variant="smallBold"
                  color="gray.500">
                  {vehicle.licensePlate}, {vehicle.model}
                </Text>
                {drivers?.map((driver) => (
                  <>
                    <Icon
                      name="id-card"
                      color="gray.400"
                    />
                    <Text
                      variant="xSmallSemiBold"
                      color="gray.400">
                      {driver?.name}
                    </Text>
                  </>
                ))}
              </HStack>
            </Stack>
            <StatusBadge
              label={status.toLocaleUpperCase()}
              iconName="clock"
              status={status}
            />
            <Text
              alignSelf="flex-start"
              variant="xSmallSemiBold"
              color="gray.400">
              {dayjs(finishedAt ?? dueDate).format('DD/MM/YYYY') ?? 'N/A'}
            </Text>
          </HStack>
        ))}
      </Stack>
    </Stack>
  );
}
